// Часовые пояса на экранах: «сейчас» по поясу психолога, перевод времени, колонки городов на шкале дня,
// переключатели поясов и окно «Пояса».
import { TZ_CITIES, minToHm, tzCity, wallClock, zonedInstant } from '@praktika/core';
import { saveSettings } from './api.ts';
import { $, $$, esc, store, toast } from './dom.ts';
import { render } from './nav.ts';
import { closeSheet, openSheet, bindSearch } from './sheet.ts';
import { S, UI } from './state.ts';

export function tzParts(d: Date, tz: string): { date: string; min: number } {
  try {
    const w = wallClock(d, tz);
    return { date: w.date, min: w.min };
  } catch {
    const iso = d.toISOString();
    return { date: iso.slice(0, 10), min: d.getUTCHours() * 60 + d.getUTCMinutes() };
  }
}

export function nowInfo(): { date: string; min: number } { return tzParts(new Date(), S.tz); }

export function convertTime(date: string, min: number, fromTz: string, toTz: string): { date: string; min: number } {
  if (fromTz === toTz) return { date, min };
  return tzParts(zonedInstant(date, minToHm(min), fromTz), toTz);
}

export function extraZones(): string[] {
  return String(S.settings.tz_extra || '').split(';').map((z) => z.trim())
    .filter((z, i, a) => z && z !== S.tz && a.indexOf(z) === i);
}
export function zonesOn(): string[] { const off = UI.tzOff || {}; return extraZones().filter((z) => !off[z]); }
// свой пояс тоже можно выключить: тогда главным становится первый включённый дополнительный
export function ownOn(): boolean { return !(UI.tzOff && UI.tzOff[S.tz]); }
export function mainZone(): string { return ownOn() ? S.tz : (zonesOn()[0] || S.tz); }
export function tlColumns(): string[] { const main = mainZone(); return zonesOn().filter((z) => z !== main).concat([main]); }

// сдвиг пояса z относительно моего в этот день, минут (положительно = там позже)
export function tzOffsetMin(date: string, z: string): number {
  const t = convertTime(date, 720, S.tz, z);
  return t.min - 720 + (t.date > date ? 1440 : t.date < date ? -1440 : 0);
}

export interface ZoneGroup { off: number; zones: string[]; main?: boolean }

// Колонки времени: города с одинаковым временем в этот день — одна колонка; главная группа — последней, её город первым.
export function zoneGroups(date: string): ZoneGroup[] {
  const cols = tlColumns(), main = cols[cols.length - 1]!, g: ZoneGroup[] = [], at: Record<number, number> = {};
  for (const z of cols) {
    const o = tzOffsetMin(date, z);
    if (!(o in at)) { at[o] = g.length; g.push({ off: o, zones: [] }); }
    g[at[o]!]!.zones.push(z);
  }
  const mg = g.splice(at[tzOffsetMin(date, main)]!, 1)[0]!;
  mg.zones = [main].concat(mg.zones.filter((z) => z !== main));
  mg.main = true;
  return g.concat([mg]);
}

export function zoneLabel(g: ZoneGroup, sep = ', '): string { return g.zones.map((z) => esc(tzCity(z))).join(sep); }
export function atOff(t: number, off: number): string { return minToHm((((t + off) % 1440) + 1440) % 1440); }

// ширина колонки времени: самое длинное название города (шрифт шапки 10.5 px) + зазор; от min до 66 px
let canvasCtx: CanvasRenderingContext2D | null = null;
function textW(s: string, px: number): number {
  try {
    canvasCtx = canvasCtx || document.createElement('canvas').getContext('2d');
    canvasCtx!.font = px + 'px ' + getComputedStyle(document.body).fontFamily;
    return canvasCtx!.measureText(s).width;
  } catch {
    return String(s).length * px * 0.56;
  }
}
export function zoneColW(groups: ZoneGroup[], min: number): number {
  let w = 0;
  for (const g of groups) for (const z of g.zones) w = Math.max(w, textW(tzCity(z), 10.5));
  return Math.max(min, Math.min(66, Math.ceil(w) + 5));
}

// Пояса на экране: включённые отмечены ✓; главный (его время крупно у сессий) залит цветом. «Пояса…» — настройка.
export function tzChipsHtml(): string {
  const off = UI.tzOff || {}, main = mainZone();
  const chip = (t: string) => {
    const on = !off[t];
    return '<button class="tzc' + (on ? ' on' : '') + (on && t === main ? ' main' : '') + '" data-tzon="' + esc(t) + '">' +
      (on ? '✓ ' : '') + esc(tzCity(t)) + '</button>';
  };
  return '<div class="tzs">' + [S.tz].concat(extraZones()).map(chip).join('') +
    '<button class="tzc set" data-tzset>Пояса…</button></div>';
}

// Показать/скрыть пояс; последний показанный скрыть нельзя.
export function toggleZone(z: string): boolean {
  UI.tzOff = UI.tzOff || {};
  if (UI.tzOff[z]) delete UI.tzOff[z];
  else {
    const left = [S.tz].concat(extraZones()).filter((x) => x !== z && !UI.tzOff![x]);
    if (!left.length) { toast('Хотя бы один пояс должен остаться'); return false; }
    UI.tzOff[z] = true;
  }
  store('praktika_tzoff', JSON.stringify(UI.tzOff));
  return true;
}

export function addZone(): void {
  tzSheet('', (v) => {
    if (v === S.tz || extraZones().includes(v)) { toast('Этот пояс уже есть'); return; }
    if (UI.tzOff) delete UI.tzOff[v];
    void saveSettings({ tz_extra: extraZones().concat([v]).join(';') }, 'Пояс добавлен');
  });
}

// Окно «Пояса»: переключатель показа, × — убрать совсем, «+ Добавить пояс».
export function zonesSheet(): void {
  const sh = openSheet('<div class="ttl">Пояса</div><p class="sub">Переключатель — показывать ли время этого города. ' +
    'Время первого показанного пояса стоит у сессий крупно, остальные — рядом мелко. × — убрать город из списка.</p>' +
    '<div class="zlist"></div><button class="opt" data-zadd><span>+ Добавить пояс</span></button>');
  const draw = () => {
    const off = UI.tzOff || {}, main = mainZone(), today = nowInfo().date;
    const diff = (z: string) => {
      if (z === S.tz) return 'ваш пояс';
      const d = tzOffsetMin(today, z), h = Math.abs(d) / 60;
      return !d ? 'то же время' : (d > 0 ? '+' : '−') + (h % 1 ? h.toFixed(1) : h) + ' ч';
    };
    $('.zlist', sh).innerHTML = [S.tz].concat(extraZones()).map((z) => '<div class="zrow"><span class="zn"><b>' +
      esc(tzCity(z)) + '</b><span class="small">' + esc(diff(z)) + (!off[z] && z === main ? ' · крупно' : '') +
      '</span></span><button class="tog' + (off[z] ? '' : ' on') + '" data-zt="' + esc(z) +
      '" aria-label="Показывать"></button>' + (z === S.tz ? '<span class="zdel"></span>' : '<button class="zdel" data-zd="' +
      esc(z) + '" aria-label="Убрать">×</button>') + '</div>').join('');
    $$('[data-zt]', sh).forEach((b) => { b.onclick = () => { if (toggleZone(b.getAttribute('data-zt')!)) { render(); draw(); } }; });
    $$('[data-zd]', sh).forEach((b) => {
      b.onclick = () => {
        const z = b.getAttribute('data-zd')!;
        if (UI.tzOff) delete UI.tzOff[z];
        void saveSettings({ tz_extra: extraZones().filter((x) => x !== z).join(';') }, 'Пояс убран');
        draw();
      };
    });
  };
  draw();
  $('[data-zadd]', sh).onclick = () => { closeSheet(); setTimeout(addZone, 50); };
}

export function tzSheet(current: string, cb: (tz: string) => void): void {
  const sh = openSheet('<div class="ttl">Часовой пояс · город</div><div class="search" style="margin:0 0 8px">' +
    '<input placeholder="Поиск города" data-q></div><div class="pick">' + TZ_CITIES.map((z) => '<button class="opt' +
    (z[0] === current ? ' ok' : '') + '" data-tz="' + esc(z[0]) + '"><span>' + esc(z[1]) + '</span><span class="hint">' +
    esc(z[0]) + '</span></button>').join('') + '</div>');
  bindSearch($<HTMLInputElement>('[data-q]', sh), sh);
  $$('[data-tz]', sh).forEach((o) => { o.onclick = () => { closeSheet(); cb(o.getAttribute('data-tz')!); }; });
}

export function tzDiffLabel(tz: string): string {
  const n = nowInfo(), t = convertTime(n.date, 12 * 60, S.tz, tz);
  const d = t.min - 720 + (t.date > n.date ? 1440 : t.date < n.date ? -1440 : 0);
  if (!d) return 'то же время';
  const h = Math.abs(d) / 60;
  return (d > 0 ? '+' : '−') + (h % 1 ? h.toFixed(1) : h) + ' ч';
}

document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  const tzb = t.closest?.('[data-tzon]');
  if (tzb) { if (toggleZone(tzb.getAttribute('data-tzon')!)) render(); return; }
  if (t.closest?.('[data-tzset]')) zonesSheet();
});
